import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { map, delay, catchError, tap, shareReplay, switchMap, share, filter, publish, takeUntil, first } from 'rxjs/operators';
import { FuelType, Meter, MeterType, ShareInfo } from '@models/meter.model';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Partner } from '@models/partner.model';
import { UtilService } from './util.service';
import { LoginService } from './login.service';
import { SharedConsent, SharedConsentApiResponse, ConsentRequest, RequestConsentAPIPayload, ConsentRequestSummary, NotificationStatus, StripeConsentPayload, StripeCredentials, StripeConsentApiResponse } from '@models/consent.model';
import { calculateConsentDateRange, calculateShareDateRange, formatDateForApi } from '@utils/meter.utils';

// Prepare request payload with proper typing
interface ConsentPayload {
  shareType: 'platform' | 'link' | 'email'; // Type of sharing
  mpxns?: string[];
  consentStartDate: string;
  consentEndDate: string;
  shareStartDate: string;
  shareStopDate: string;
  requestId?: string;
  consentRequestId?: string; // Make this optional in the type
  customerEmail: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConsentService {
  private readonly loginService = inject(LoginService);
  private readonly http = inject(HttpClient);
  private readonly utilService = inject(UtilService);

  get agentContext(): Partner | null {
    const context = sessionStorage.getItem('agentContext');
    if (context) {
      try {
        return JSON.parse(context) as Partner;
      } catch (e) {
        console.error('Error parsing agent context from sessionStorage:', e);
        return null;
      }
    }
    return null;
  }

  set agentContext(context: Partner | null) {
    sessionStorage.setItem('agentContext', JSON.stringify(context));
  }

  private softwareProviders$: Observable<Partner[]> | null = null; // Cache for software providers
  
  private endPointUrl = environment.baseUrl + '/core';
  
  constructor() { 
    console.log('ConsentService initialized');
  }

  /**
   * Get all shared consents from the API
   * @returns Observable<SharedConsent[]>
   */
  getAllSharedConsents(): Observable<SharedConsent[]> {
    const url = `${this.endPointUrl}/v1/customer/shared-consents`;

    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        if (!email) {
          console.error('User email not available. User may not be logged in.');
          return of([]);
        }
        return this.http.post<SharedConsentApiResponse>(url, { email }, {
          headers: {
            'Content-Type': 'application/json'
          }
        }).pipe(
          map(response => {
            if (response.status && response.data?.sharedConsents) {
              return response.data.sharedConsents.map(consent => ({
                shareId: consent.shareId,
                consentRequestId: consent.consentRequestId,
                mpans: consent.mpxns || [],
                meterNo: consent.mpxns.length > 1 ? 'MULTIPLE' : consent.mpxns[0], // Use first MPAN as meterNo
                address: consent.siteAddress,
                meterType: consent.meterType || consent.scope,
                fuelType: consent.fuelType,
                consentStatus: consent.status,
                partnerEmail: consent.agentEmail,
                shareType: consent.shareType,
                softwarePlatform: consent.softwareName || '*',
                emailSentTo: consent.emailSentTo?.map(emailInfo => emailInfo.email).join(', ') || '*',
                dataLink: `${environment.openDataApiUrl}/core/v1/data-download?token=${consent.shareId}`, 
                partnerOrg: consent.agentOrg,
                consentStartDate: consent.consentStartDate ? new Date(consent.consentStartDate) : undefined,
                consentEndDate: consent.consentStopDate ? new Date(consent.consentStopDate) : undefined,
                createdAt: consent.createdAt ? new Date(consent.createdAt) : undefined
              }));
            }
            return [];
          }),
          catchError(error => {
            console.error('Error fetching all shared consents:', error);
            return of([]);
          })
        );
      }),
      shareReplay(1)
    );
  }

  /**
   * Fetch all shared consents from the API
   * @returns Observable<SharedConsent[]>
   */
  /**
   * Get shared consents for a specific meter (filtered from getAllSharedConsents)
   * @param meterNo The meter number to filter by
   * @returns Observable<SharedConsent[]>
   */
  getSharedConsentsByMeter(meterNo: string): Observable<SharedConsent[]> {
    return this.getAllSharedConsents().pipe(
      map(consents => consents.filter(consent => consent.mpans.includes(meterNo)))
    );
  }

  /**
   * Revoke access for a specific shared consent
   * @param shareId The share ID to revoke access for
   * @param meterNumbers Optional array of meter numbers to revoke access for
   * @returns Observable<boolean>
   */
  revokeAccess(shareId: string, meterNumbers?: string[]): Observable<boolean> {
    const url = `${this.endPointUrl}/v1/customer/revoke-shared-consent`;

    return this.http.post<any>(url, {
      shareId,
      mpxns: meterNumbers
    }).pipe(
      map(response => {
        console.log(`Access revoked for share ID: ${shareId}`);
        return response.status === true;
      }),
      catchError(error => {
        console.error('Error revoking access:', error);
        return of(false);
      })
    );
  }

  /**
   * Request consent from an organization
   * @param orgName The organization name to request consent from
   * @param meterNumber The meter number for the consent request
   * @param agentOrgId Optional agent organization ID
   * @returns Observable<string> The new consentRequestId
   */
  requestConsent(orgName: string, meters: Meter[], agentOrgId?: string): Observable<string> {
    const url = `${this.endPointUrl}/v1/request-consent`;

    return this.loginService.loginUserEmail.pipe(
      switchMap(customerEmail => {
        if (!customerEmail) {
          console.error('Cannot request consent: No user email available');
          return of('');
        }

        // const {mpan, mprn} = this.utilService.getMeterBreakdown(meters);
        
        const payload: RequestConsentAPIPayload = {
          sendEmail: false,
          agentOrg: orgName,
          customerEmail: customerEmail,
          meters: meters.map(m => ({
            mpxn: m.meterNumber,
            fuelType: m.fuelType.charAt(0).toUpperCase(), // Capitalize fuel type
          }))
        };

        // Add agentOrgId to payload if provided
        if (agentOrgId) {
          payload.agentOrgId = agentOrgId;
        }

        console.log('Requesting consent with payload:', payload);
        
        return this.http.post<any>(url, payload, {
          headers: {
            'Content-Type': 'application/json'
          }
        }).pipe(
          map(response => {
            if (response.status && response.data?.consentRequestId) {
              console.log(`Consent requested from "${orgName}" successfully. ConsentRequestId: ${response.data.consentRequestId}`);
              return response.data.consentRequestId;
            } else {
              console.error('Error requesting consent:', response);
              return '';
            }
          }),
          delay(500), // add a delay
        );
      })
    );
  }

  /**
   * Add a new organization by requesting consent
   * @param orgName The organization name to add
   * @param meterNumber The meter number for the consent request
   * @returns Observable<string> The new consentRequestId
   */
  addOrg(orgName: string, meters: Meter[]): Observable<string> {
    return this.requestConsent(orgName, meters);
  }

  // Method to get all software providers with preferred flag set based on agent context
  getAllOrganizations(): Observable<Partner[]> {
    // If we haven't cached the software providers yet, fetch them
    if (!this.softwareProviders$) {
      this.softwareProviders$ = this.http.get<any>(`${environment.baseUrlSoftwareProviders}/core/v1/software-providers`).pipe(
        map(response => {
          if (response.status && response.message) {
            return response.message.map((provider: any) => ({
              id: provider.orgId.toString(),
              name: provider.orgName,
              email: '', // Email not provided in the response, will need to be filled later
              software: provider.softwareName,
              isPreferred: false
            } as Partner));
          }
          return [];
        }),
        shareReplay(1), // Cache the result
        catchError(error => {
          console.error('Error fetching software providers:', error);
          return of([]);
        })
      );
    }

    let softwareProviders$ = this.softwareProviders$;

    // If agent context exists, set isPreferred flag for matching provider
    if (this.agentContext) {
      softwareProviders$ = softwareProviders$.pipe(
      map(providers => {
        return providers.map(provider => ({
          ...provider,
          isPreferred: provider.name?.trim().toLowerCase() === this.agentContext?.name.trim().toLowerCase()
        }));
      })
      );
    }

    // Return all providers with sorting: preferred first, then alphabetically by software name
    return softwareProviders$.pipe(
      map(providers => {
        return providers.sort((a, b) => {
          // Preferred organizations come first
          if (a.isPreferred && !b.isPreferred) return -1;
          if (!a.isPreferred && b.isPreferred) return 1;

          // For non-preferred or both preferred, sort alphabetically by software name
          return (a.software || '').localeCompare(b.software || '', undefined, {
            sensitivity: 'base'
          });
        });
      }),
      delay(300) // Add delay to simulate network call
    );
  }

  // Method to share meter with partner
  /**
   * Share meters with a partner
   * @param meters The meters to share (array)
   * @param partner The partner to share with
   * @param consentId The consent ID associated with the meters (using first meter's consentId)
   * @param consentRequestId Optional consent request ID - if not provided, will request consent first
   * @returns Observable<boolean>
   */
  shareMeterWithPartner(meters: Meter[], partner: Partner, requestId?: string, consentRequestId?: string): Observable<string | null> {
    const meterNumbers = meters.map(m => m.meterNumber).join(', ');
    const meterCount = meters.length;
    console.log(`Sharing ${meterCount} meter(s) [${meterNumbers}] with partner ${partner.name} (${partner.email})`);
    
    // If consentRequestId is provided, use it directly
    if (consentRequestId) {
      console.log(`Using provided consent request ID: ${consentRequestId}`);
      return this.shareWithConsentRequestId(meters, consentRequestId, requestId);
    }
    
    // If consentRequestId is not provided, request consent first
    // For multi-meter scenario, use the first meter for consent request
    return this.requestConsent(partner.name, meters, partner.id).pipe(
      switchMap(newConsentRequestId => {
        if (!newConsentRequestId) {
          console.error('Failed to get consent request ID, cannot share meters');
          return of(null);
        }
        console.log(`Obtained consent request ID: ${newConsentRequestId}`);
        return this.shareWithConsentRequestId(meters, newConsentRequestId, requestId);
      })
    );
  }

  /**
   * Helper method to share meters with a consent request ID
   * @param meters The meters to share (array)
   * @param partner The partner to share with
   * @param consentId The consent ID associated with the meters
   * @param consentRequestId The consent request ID (mandatory)
   * @returns Observable<boolean>
   */
  private shareWithConsentRequestId(meters: Meter[], consentRequestId: string, requestId?: string): Observable<string | null> {
    // For multi-meter sharing, we'll create multiple shared consent entries but make a single API call
    const meterNumbers = meters.map(m => m.meterNumber);
    const primaryMeter = meters[0]; // Use first meter for dates and general info
    
    // Calculate default share dates
    const { startDate: shareStartDate, endDate: shareStopDate } = calculateShareDateRange();

    // Use the helper method to make the API call with all meter numbers
    return this.makeMeterDataSharingCall(
      'platform', // Share type is platform
      meterNumbers,
      primaryMeter.startDate,
      primaryMeter.expiryDate,
      shareStartDate,
      shareStopDate,
      false, // Not downloadable
      requestId,
      consentRequestId
    ).pipe(
      map(response => {
        if (response.status) {
          console.log(`Successfully shared ${meters.length} meters with partner:`, response);
          return response.data?.shareId || null; // Return shareId if available
        } else {
          console.error('Error sharing meters:', response);
          return of(null);
        }
      }),
      catchError(error => {
        console.error('Error sharing meters:', error);
        return of(null);
      })
    );
  }

  /**
   * Private helper method to make meter data sharing API call
   * @param meterNumbers Array of meter numbers (MPANs)
   * @param consentStartDate Consent start date
   * @param consentStopDate Consent stop date  
   * @param shareStartDate Share start date
   * @param shareStopDate Share stop date
   * @param consentRequestId Optional consent request ID
   * @returns Observable<any> Raw API response
   */
  private makeMeterDataSharingCall(
    shareType: 'platform' | 'link' | 'email',
    meterNumbers: string[],
    consentStartDate: Date,
    consentEndDate: Date,
    shareStartDate: Date,
    shareStopDate: Date,
    isDownloadable = false,
    requestId?: string,
    consentRequestId?: string,
  ): Observable<any> {
    const url = `${this.endPointUrl}/v1/customer/meter-data-sharing`;

    return this.loginService.loginUserEmail.pipe(
      switchMap(customerEmail => {
        if (!customerEmail) {
          console.error('Cannot make meter data sharing call: No user email available');
          return throwError(() => new Error('User email not available'));
        }

        const payload: ConsentPayload = {
          shareType,
          customerEmail,
          mpxns: meterNumbers,
          consentStartDate: formatDateForApi(consentStartDate),
          consentEndDate: formatDateForApi(consentEndDate),
          shareStartDate: formatDateForApi(shareStartDate),
          shareStopDate: formatDateForApi(shareStopDate),
        };

        // Add consentRequestId to payload if provided
        if (consentRequestId) {
          payload.consentRequestId = consentRequestId;
          payload.mpxns = undefined;
        }

        if (requestId) {
          payload.requestId = requestId; // Use requestId as consentId
        }

        return this.http.post<any>(url, payload, {
          headers: {
            'Content-Type': 'application/json'
          },
          params: {
            'generateFiles': isDownloadable ? 'true' : 'false'
          }
        });
      }),
      tap((response) => {
        console.log(`Meter data sharing call made successfully for shareType: ${shareType}`);
      })
    );
  }

  /**
   * Get consent counts by meter
   * @returns Observable<Map<string, number>> A map of meter numbers to their share counts
   */
  getConsentCountsByMeter(): Observable<Map<string, number>> {
    return this.getAllSharedConsents().pipe(
      map(consents => {
        // Count shares per meter
        const shareCount = new Map<string, number>();
        
        // Build map of meter numbers to share counts
        consents.forEach(consent => {
          // Iterate through each meter in the consent's mpans array
          consent.mpans.forEach(meterNo => {
            const count = shareCount.get(meterNo) || 0;
            shareCount.set(meterNo, count + 1);
          });
        });
        
        return shareCount;
      })
    );
  }

  /**
   * Get active consent counts by meter
   * @returns Observable<Map<string, number>> A map of meter numbers to their active share counts
   */
  getActiveConsentCountsByMeter(): Observable<Map<string, number>> {
    return this.getAllSharedConsents().pipe(
      map(consents => {
        // Count only active shares per meter
        const activeShareCount = new Map<string, number>();
        
        // Build map of meter numbers to active share counts
        consents.forEach(consent => {
          // Only count if consent status is active
          if (consent.consentStatus && consent.consentStatus.toLowerCase() === 'active') {
            // Iterate through each meter in the consent's mpans array
            consent.mpans.forEach(meterNo => {
              const count = activeShareCount.get(meterNo) || 0;
              activeShareCount.set(meterNo, count + 1);
            });
          }
        });
        
        return activeShareCount;
      })
    );
  }

  /**
   * Get active sharee information by meter
   * @returns Observable<Map<string, ShareInfo[]>> A map of meter numbers to their active share details
   */
  getActiveShareeNamesByMeter(): Observable<Map<string, ShareInfo[]>> {
    return this.getAllSharedConsents().pipe(
      map(consents => {
        // Build map of meter numbers to active share information
        const shareInfoByMeter = new Map<string, ShareInfo[]>();
        
        consents.forEach(consent => {
          // Only include if consent status is active and has shareId
          if (consent.consentStatus && consent.consentStatus.toLowerCase() === 'active' && consent.shareId) {
            let shareeName = '';
            
            // Determine sharee name based on share type
            if (consent.shareType === 'email') {
              shareeName = consent.emailSentTo || '';
            } else if (consent.shareType === 'platform') {
              // Build sharee name based on available information
              const softwarePlatform = consent.softwarePlatform && consent.softwarePlatform !== '*' ? consent.softwarePlatform : '';
              const partnerOrg = consent.partnerOrg && consent.partnerOrg !== '*' ? consent.partnerOrg : '';
              
              if (softwarePlatform && partnerOrg) {
                // Both present: display as "softwareName (partnerOrg)"
                shareeName = `${softwarePlatform} (${partnerOrg})`;
              } else if (softwarePlatform) {
                // Only software platform present
                shareeName = softwarePlatform;
              } else if (partnerOrg) {
                // Only partner org present
                shareeName = partnerOrg;
              } else {
                shareeName = '';
              }
            } else if (consent.shareType === 'link') {
              // For link shares, we don't show a name but still count them
              shareeName = 'SHAREABLE LINK';
            }
            
            // Add share info for each meter in the consent's mpans array
            consent.mpans.forEach(meterNo => {
              const existingShares = shareInfoByMeter.get(meterNo) || [];
              
              // Check if this shareId already exists to avoid duplicates
              const shareExists = existingShares.some(share => share.shareId === consent.shareId);
              
              if (!shareExists) {
                const shareInfo: ShareInfo = {
                  shareId: consent.shareId!,
                  shareType: consent.shareType as 'platform' | 'email' | 'link',
                  shareeName: shareeName,
                  consentStartDate: consent.consentStartDate!,
                  consentEndDate: consent.consentEndDate!
                };
                
                existingShares.push(shareInfo);
                shareInfoByMeter.set(meterNo, existingShares);
              }
            });
          }
        });
        
        return shareInfoByMeter;
      })
    );
  }

  /**
   * Generate a shareable link for one or more meters
   * @param meterNumbers The MPxNs to include in the generated download
   * @returns Observable<string> The shareable link
   */
  generateShareableLink(shareType: 'link' | 'email', meterNumbers: string[], requestId?: string, consentRequestId?: string): Observable<string> {
    const { startDate, endDate } = calculateConsentDateRange();
    const { startDate: shareStartDate, endDate: shareStopDate } = calculateShareDateRange();

    return this.makeMeterDataSharingCall(
      shareType,
      meterNumbers,
      startDate,
      endDate,
      shareStartDate,
      shareStopDate,
      true, // Set isDownloadable to true for shareable link,
      requestId,
      consentRequestId
    ).pipe(
      map(response => {
        if (response.status && response.data?.shareId) {
          // Construct the download URL using the token
          return `${environment.openDataApiUrl}/core/v1/data-download?token=${response.data.shareId}`;
        }
        throw new Error('Failed to generate shareable link');
      }),
      catchError(error => {
        console.error('Error generating shareable link:', error);
        throw error;
      })
    );
  }

  pollDownloadReady(url: string, intervalMs = 3000, timeoutMs = 60000): Observable<string> {
    return timer(0, intervalMs).pipe(
      takeUntil(timer(timeoutMs)),
      switchMap(() =>
        this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
          catchError(error => {
            if (error?.status === 400) {
              return of(null);
            }
            throw error;
          })
        )
      ),
      filter((response): response is HttpResponse<Blob> => response !== null && response.status === 200),
      first(),
      map(() => url)
    );
  }

  /**
   * Share data to email using shareId
   * @param shareId The share ID obtained from generateShareableLink
   * @param email The email address to send data to
   * @returns Observable<boolean> Success status
   */
  shareDataToEmail(shareId: string, receiverEmail: string): Observable<boolean> {
    const url = `${this.endPointUrl}/v1/customer/data-share`;

    const payload = {
      shareId,
      receiverEmail
    };

    return this.http.post<any>(url, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).pipe(
      map(response => {
        if (response.status) {
          console.log(`Data successfully shared to email: ${receiverEmail}`);
          return true;
        }
        throw new Error('Failed to share data to email');
      }),
      catchError(error => {
        console.error('Error sharing data to email:', error);
        throw error;
      })
    );
  }

  /**
   * Fetch consent request details by consentRequestId
   * @param consentRequestId The consent request ID to fetch details for
   * @returns Observable<ConsentRequest>
   */
  getConsentRequestById(consentRequestId: string): Observable<ConsentRequest> {
    console.log(`Fetching consent request details for ID: ${consentRequestId}`);
    
    const url = `${this.endPointUrl}/v1/consent-requests?consentRequestId=${consentRequestId}`;
    
    return this.http.get<any>(url).pipe(
      map(response => {
        if (response.status && response.data?.consentRequests?.length > 0) {
          // Always use the first consent request
          const consentRequest = response.data.consentRequests[0] as ConsentRequest;
          
          return consentRequest;
        }
        throw new Error('Failed to fetch consent request details');
      }),
      catchError(error => {
        console.error('Error fetching consent request:', error);
        throw error;
      })
    );
  }

  /**
   * Fetch all consent requests for the current user
   * @returns Observable<ConsentRequestSummary[]>
   */
  getConsentRequestByUser(): Observable<ConsentRequestSummary[]> {
    console.log('Fetching consent requests for current user');
    
    return this.loginService.loginUserEmail.pipe(
      switchMap(email => {
        if (!email) {
          console.error('User email not available. User may not be logged in.');
          return of([]);
        }

        const url = `${this.endPointUrl}/v1/consent-requests?email=${encodeURIComponent(email)}`;
        
        return this.http.get<any>(url).pipe(
          map(response => {
            if (response.status && response.data?.consentRequestsSummary) {
              console.log(`Found ${response.data.totalConsentRequests} consent request(s) for user`);
              return response.data.consentRequestsSummary as ConsentRequestSummary[];
            }
            return [];
          }),
          catchError(error => {
            console.error('Error fetching user consent requests:', error);
            return of([]);
          })
        );
      })
    );
  }

  /**
   * Helper method to determine if a ConsentRequest is for single or multiple meters
   * @param consentRequest The consent request to check
   * @returns boolean - true if single meter, false if multiple meters
   */
  isSingleMeterRequest(consentRequest: ConsentRequest): boolean {
    return consentRequest.meters.length === 1;
  }

  /**
   * Helper method to get the first meter number from a ConsentRequest
   * Useful for single meter flows
   * @param consentRequest The consent request
   * @returns string - the first meter number or empty string if no meters
   */
  getFirstMeterFromRequest(consentRequest: ConsentRequest): string {
    return consentRequest.meters.length > 0 ? consentRequest.meters[0].mpxn : '';
  }

  /**
   * Helper method to get all meter numbers from a ConsentRequest
   * @param consentRequest The consent request
   * @returns string[] - array of all meter numbers
   */
  getAllMetersFromRequest(consentRequest: ConsentRequest): string[] {
    return consentRequest.meters.map(m => m.mpxn);
  }

  /**
   * Enhanced method to get consent request with flow information
   * @param consentRequestId The consent request ID to fetch details for
   * @returns Observable<ConsentRequest & { isSingleMeter: boolean }>
   */
  getConsentRequestWithFlowInfo(consentRequestId: string): Observable<ConsentRequest & { isSingleMeter: boolean }> {
    return this.getConsentRequestById(consentRequestId).pipe(
      map(consentRequest => ({
        ...consentRequest,
        isSingleMeter: this.isSingleMeterRequest(consentRequest)
      }))
    );
  }

  /**
   * Update notification status for a consent request
   * @param consentRequestId The consent request ID to update
   * @param notificationStatus The new status (NotificationStatus enum) - case insensitive
   * @returns Observable<boolean> Success status
   */
  updateNotificationStatus(consentRequestId: string, notificationStatus: NotificationStatus): Observable<boolean> {
    const url = `${this.endPointUrl}/v1/notification-status-update`;
    
    const payload = {
      consentRequestId: consentRequestId,
      notificationStatus: notificationStatus.toLowerCase()
    };

    console.log(`Updating notification status for consent request ${consentRequestId} to ${notificationStatus}`);
    
    return this.http.post<any>(url, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).pipe(
      map(response => {
        if (response.status) {
          console.log(`Successfully updated notification status for ${consentRequestId} to ${notificationStatus}`);
          return true;
        } else {
          console.error('Error updating notification status:', response);
          return false;
        }
      }),
      catchError(error => {
        console.error('Error updating notification status:', error);
        return of(false);
      })
    );
  }


  getStripeCredentials(payload: StripeConsentPayload): Observable<StripeCredentials> {
    const url = `${this.endPointUrl}/v1/customer/submit-smart-consent`;

    console.log('Submitting smart consent for Stripe credentials:', payload);

    return this.http.post<StripeConsentApiResponse>(url, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).pipe(
      map(response => {
        if (response.status && response.message) {
          console.log('Successfully obtained Stripe credentials');
          return {
            stripeCustomerId: response.message.stripeCustomerId,
            stripeSetupIntentId: response.message.stripeSetupIntentId,
            stripeSecret: response.message.stripeSecret,
            publishable_key: response.message.publishable_key
          };
        }
        throw new Error('Failed to obtain Stripe credentials');
      }),
      catchError(error => {
        console.error('Error obtaining Stripe credentials:', error);
        throw error;
      })
    );
  }
}
